import { useState } from "react";
import { Button } from "@/components/catalyst/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/catalyst/dialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { adminResetUserPasswordMutation } from "@/app/Users/GraphQL/userMutations";
import { toast } from "sonner";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";

export function ResetPasswordDialog({
  userId,
  userName,
}: {
  userId: string;
  userName: string | null;
}) {
  const { close } = useDialogState();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useGqlMutation(
    adminResetUserPasswordMutation,
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    await mutateAsync({ id: userId, password });
    toast.success("Password has been reset");
    close();
  }

  return (
    <Dialog open onClose={close}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Reset password for {userName ?? "user"}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                autoComplete="new-password"
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={close} type="button">
            Cancel
          </Button>
          <Button color="red" type="submit" disabled={isPending}>
            {isPending ? "Resetting..." : "Reset password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
